import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { PLAYER_Z_INDEX } from '@unicons-constants/style.constants';
import { mediaQuery, Spacings } from '@styles/index';
import Image from '@base/Image';
import { useLessonPlayerStore } from '@stores/lessonPlayerStore';
import { handleControls } from '@utils/unacademyIcons/playerHelper';
import { PLAYER_CONTROLS } from 'types/unacademyIcons/player.types';
import useWindowSize from '@hooks/useWindowSize';
import ControlButton from './ControlButton';

interface PrimaryControlsProps {
  isMobileControls: boolean;
  isMobileDevice: boolean;
}

type ControlsWrapperProps = {
  $isVisible?: boolean;
  $isMobileDevice?: boolean;
  $isMobileControls: boolean;
};

const ControlsWrapper = styled.div<ControlsWrapperProps>`
  display: ${({ $isMobileDevice, $isMobileControls }) =>
    ($isMobileDevice && $isMobileControls) ||
    (!$isMobileDevice && !$isMobileControls)
      ? 'flex'
      : 'none'};
  position: ${({ $isMobileDevice }) =>
    $isMobileDevice ? 'absolute' : 'relative'};
  left: 0;
  margin: auto;
  right: 0;
  z-index: ${PLAYER_Z_INDEX.BUTTONS_WRAPPER};
  height: ${({ $isMobileControls }) => ($isMobileControls ? '100%' : 'auto')};
  width: ${({ $isMobileControls }) => ($isMobileControls ? '100%' : 'auto')};
  flex-grow: ${({ $isMobileControls }) => ($isMobileControls ? '0' : '1')};
  justify-content: ${({ $isMobileControls }) =>
    $isMobileControls ? 'center' : 'flex-start'};
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  transition: ${({ $isVisible }) =>
    $isVisible
      ? 'opacity 100ms ease-out 0s'
      : 'opacity 1000ms ease-out 0s'} !important;
`;

const ControlsButtons = styled.div<ControlsWrapperProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 336px;
  width: ${({ $isMobileControls }) => ($isMobileControls ? '100%' : 'auto')};
  ${mediaQuery.uptoMobile} {
    justify-content: space-around;
  }
`;

const PrimaryControls: React.FC<PrimaryControlsProps> = ({
  isMobileControls,
  isMobileDevice
}) => {
  const { player, isFullscreen, isControlsVisible, lessonSlug } =
    useLessonPlayerStore();
  const { isMobile } = useWindowSize();

  const renderPlayPauseIcon = useCallback(() => {
    try {
      if (player.paused()) {
        return 'play.svg';
      }
    } catch {
      return 'play.svg';
    }
    return 'pause-video.svg';
  }, [player]);

  const renderPlayPauseSize = useCallback(() => {
    if (isMobile || !isFullscreen) return '24px';
    if (isMobileDevice) return '32px';
    return '32px';
  }, [isMobileDevice, isFullscreen, isMobile]);

  const renderDurationSize = useCallback(() => {
    if (isMobile || !isFullscreen) return '24px';
    if (isMobileDevice) return '30px';
    return '30px';
  }, [isMobileDevice, isFullscreen, isMobile]);

  const renderPlayTooltip = useCallback(() => {
    try {
      if (player.paused()) {
        return 'Play (k)';
      }
    } catch {
      return 'Play (k)';
    }

    return 'Pause (k)';
  }, [player]);

  return (
    <ControlsWrapper
      $isMobileDevice={isMobileDevice}
      $isVisible={isControlsVisible}
      $isMobileControls={isMobileControls}
    >
      <ControlsButtons $isMobileControls={isMobileControls}>
        {(lessonSlug || !isMobile) && (
          <ControlButton
            tooltipTitle="Rewind 10"
            onClick={() => handleControls(PLAYER_CONTROLS.BACK_10, player)}
          >
            <Image
              src="rewind-video.svg"
              alt="Rewind 10"
              layout="intrinsic"
              width={renderDurationSize()}
              height={renderDurationSize()}
            />
          </ControlButton>
        )}

        <ControlButton
          tooltipTitle={renderPlayTooltip()}
          onClick={(e) => {
            e.stopPropagation();
            handleControls(PLAYER_CONTROLS.PLAY_PAUSE, player);
          }}
        >
          <Image
            src={renderPlayPauseIcon()}
            alt="Play/Pause"
            layout="intrinsic"
            width={renderPlayPauseSize()}
            height={renderPlayPauseSize()}
          />
        </ControlButton>
        {(lessonSlug || !isMobile) && (
          <ControlButton
            tooltipTitle="Forward 10"
            marginRight={isMobileDevice ? '0px' : Spacings.SPACING_3B}
            onClick={() => handleControls(PLAYER_CONTROLS.FORWARD_10, player)}
          >
            <Image
              src="forward-video.svg"
              alt="Forward 10"
              layout="intrinsic"
              width={renderDurationSize()}
              height={renderDurationSize()}
            />
          </ControlButton>
        )}
      </ControlsButtons>
    </ControlsWrapper>
  );
};

PrimaryControls.displayName = 'Primary Controls';

export default PrimaryControls;
